import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useOutletContext } from "react-router-dom";
import { useSnackbar } from "notistack";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";

import EntityTable, { RowActionMenu } from "components/EntityTable";
import ConfirmDialog from "components/ConfirmDialog";
import SidePanel from "components/SidePanel";
import { getContentType } from "config/contentTypeRegistry";
import { useContentTypes, useContentTypeId } from "context/ContentTypesContext";
import {
  deleteContent,
  publishContent,
  unpublishContent,
  archiveContent,
} from "api/endpoints/content";
import { formatDate } from "utils/datetime";

import useContentList from "./useContentList";
import ContentForm from "./ContentForm";

const STATUS_COLORS = { draft: "secondary", published: "success", archived: "dark" };

const ContentStatusBadge = ({ status }) => (
  <MDBadge
    badgeContent={status || "draft"}
    color={STATUS_COLORS[status] || "secondary"}
    variant="gradient"
    size="sm"
    container
  />
);
ContentStatusBadge.propTypes = { status: PropTypes.string };
ContentStatusBadge.defaultProps = { status: "draft" };

/**
 * Batch 2.4 — generic content list screen. One mounted instance per content
 * type (the `typeKey` prop selects the registry entry). Overwrites the Batch-1
 * placeholder.
 */
function ContentListPage({ typeKey }) {
  const entry = getContentType(typeKey);
  const { websiteId } = useOutletContext();
  const { status: typesStatus, reload } = useContentTypes();
  const contentTypeId = useContentTypeId(entry.pathSlug);
  const { enqueueSnackbar } = useSnackbar();

  const list = useContentList({ websiteId, contentTypeId });
  const {
    items,
    total,
    loading,
    page,
    pageSize,
    setPage,
    setPageSize,
    status,
    setStatus,
    setSearch,
  } = list;

  const [searchInput, setSearchInput] = useState("");
  const [panel, setPanel] = useState({ open: false, contentId: null });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce the search box → server query.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const openCreate = () => setPanel({ open: true, contentId: null });
  const openEdit = (row) => setPanel({ open: true, contentId: row.content_id });
  const closePanel = () => setPanel({ open: false, contentId: null });

  const runRowLifecycle = async (action, row, label) => {
    try {
      await action(websiteId, row.content_id);
      enqueueSnackbar(label, { variant: "success" });
      list.refresh();
    } catch {
      enqueueSnackbar(`Could not ${label.toLowerCase()}`, { variant: "error" });
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteContent(websiteId, confirmDeleteId);
      enqueueSnackbar(`${entry.singularLabel} deleted`, { variant: "success" });
      list.refresh();
    } catch {
      enqueueSnackbar("Failed to delete content", { variant: "error" });
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const rowActions = (row) => {
    const actions = [{ label: "Edit", icon: "edit", onClick: () => openEdit(row) }];
    if (row.status !== "published") {
      actions.push({
        label: "Publish",
        icon: "publish",
        onClick: () => runRowLifecycle(publishContent, row, "Published"),
      });
    }
    if (row.status === "published") {
      actions.push({
        label: "Unpublish",
        icon: "unpublished",
        onClick: () => runRowLifecycle(unpublishContent, row, "Unpublished"),
      });
    }
    if (row.status !== "archived") {
      actions.push({
        label: "Archive",
        icon: "archive",
        onClick: () => runRowLifecycle(archiveContent, row, "Archived"),
      });
    }
    actions.push({
      label: "Delete",
      icon: "delete",
      color: "error",
      onClick: () => setConfirmDeleteId(row.content_id),
    });
    return actions;
  };

  const columns = [
    { field: "title", headerName: "Title", flex: 1.4, minWidth: 200 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 120,
      renderCell: ({ value }) => <ContentStatusBadge status={value} />,
    },
    {
      field: "updated_at",
      headerName: "Updated",
      flex: 0.8,
      minWidth: 130,
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "published_at",
      headerName: "Published",
      flex: 0.8,
      minWidth: 130,
      renderCell: ({ value }) => formatDate(value),
    },
    ...(entry.extraColumns || []),
    {
      field: "actions",
      headerName: "",
      width: 64,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => <RowActionMenu actions={rowActions(row)} />,
    },
  ];

  if (typesStatus === "loading") {
    return (
      <MDBox display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (typesStatus === "error") {
    return (
      <Card sx={{ p: 6 }}>
        <MDBox textAlign="center">
          <MDTypography variant="h5" color="error" mb={1}>
            Could not load content types
          </MDTypography>
          <MDButton variant="outlined" color="info" onClick={reload}>
            Retry
          </MDButton>
        </MDBox>
      </Card>
    );
  }

  return (
    <MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <MDTypography variant="h5" fontWeight="bold">
          {entry.label}
        </MDTypography>
        <MDButton variant="gradient" color="info" startIcon={<Icon>add</Icon>} onClick={openCreate}>
          New {entry.singularLabel}
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <MDBox display="flex" gap={2} flexWrap="wrap" mb={2}>
            <MDInput
              placeholder="Search content"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              InputProps={{ endAdornment: <Icon fontSize="small">search</Icon> }}
              sx={{ minWidth: 260 }}
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
          </MDBox>

          <EntityTable
            rows={items}
            columns={columns}
            getRowId={(row) => row.content_id}
            loading={loading}
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onRowClick={({ row }) => openEdit(row)}
          />
        </MDBox>
      </Card>

      <SidePanel
        open={panel.open}
        onClose={closePanel}
        title={panel.contentId ? `Edit ${entry.singularLabel}` : `New ${entry.singularLabel}`}
        subtitle={`Author and manage ${entry.label.toLowerCase()} for this website`}
        width={760}
      >
        {panel.open && (
          <ContentForm
            key={panel.contentId || "new"}
            typeKey={typeKey}
            websiteId={websiteId}
            contentId={panel.contentId}
            onSaved={list.refresh}
          />
        )}
      </SidePanel>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title={`Delete ${entry.singularLabel}`}
        message="Are you sure you want to delete this content? This cannot be undone."
        confirmText="Delete"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setConfirmDeleteId(null)}
      />
    </MDBox>
  );
}

ContentListPage.propTypes = {
  typeKey: PropTypes.string.isRequired,
};

export default ContentListPage;
